import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  FileText,
  Download,
  Eye,
  Calendar as CalendarIcon,
  Filter,
  X,
  RefreshCw,
  FileSpreadsheet,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatementsPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState(null);

  const fetchStatements = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('statement_type', typeFilter);
      if (dateRange.from) params.append('start_date', dateRange.from.toISOString());
      if (dateRange.to) params.append('end_date', dateRange.to.toISOString());

      const response = await axios.get(`${API_URL}/api/statements?${params}`, { headers });
      setStatements(response.data);
    } catch (error) {
      console.error('Failed to fetch statements:', error);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, dateRange, token]);

  useEffect(() => {
    fetchStatements();
  }, [fetchStatements]);

  // Normalize backend status into a UI-friendly value. Statements stored with
  // status "generated"/"generated_no_upload" are still downloadable because the
  // backend regenerates the PDF on the fly from rendered_html.
  const getStatus = (stmt) => {
    const s = stmt?.status || '';
    if (s === 'available' || s === 'generated' || s === 'generated_no_upload') return 'available';
    return 'processing';
  };

  const handleDownload = async (statement) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/statements/${statement.id}/download`,
        { 
          headers,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statement_${statement.period_label.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download statement:', error);
    }
  };

  const handlePreview = async (statement) => {
    // Open the HTML statement in a NEW BROWSER TAB, not a modal.
    // Uses the /view-html endpoint which returns the rendered HTML.
    // Fallback to the download endpoint if no HTML endpoint exists.
    try {
      const r = await axios.get(
        `${API_URL}/api/statements/${statement.id}/view-html`,
        { headers, responseType: 'text' }
      );
      const html = typeof r.data === 'string' ? r.data : String(r.data);
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Keep the blob alive long enough for the tab to fetch it
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      // Fallback: use the raw statement_html if present on the record
      if (statement.rendered_html) {
        const blob = new Blob([statement.rendered_html], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        return;
      }
      console.error('Failed to open HTML statement:', error);
      // Ultimate fallback — the old modal so the user isn't stranded
      setSelectedStatement(statement);
      setPreviewOpen(true);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Render in UTC to avoid the SAST off-by-one problem: `period_end` is
    // stored as YYYY-MM-DDT23:59:59Z, which converts to 01:59 the NEXT day
    // in local time and misleadingly shows "1 Jul" for a June statement.
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'monthly': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'quarterly': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'annual': return 'bg-green-100 text-green-700 border-green-200';
      case 'adhoc': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'monthly': return 'Monthly Statement';
      case 'quarterly': return 'Quarterly Summary';
      case 'annual': return 'Annual Summary';
      case 'adhoc': return 'Ad-hoc Statement';
      default: return type;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'monthly': return <FileText className="h-8 w-8 text-blue-600" />;
      case 'quarterly': return <FileSpreadsheet className="h-8 w-8 text-purple-600" />;
      case 'annual': return <FileSpreadsheet className="h-8 w-8 text-green-600" />;
      default: return <FileText className="h-8 w-8 text-gray-600" />;
    }
  };

  const clearFilters = () => {
    setTypeFilter('all');
    setDateRange({ from: null, to: null });
  };

  const hasFilters = typeFilter !== 'all' || dateRange.from || dateRange.to;

  // Group statements by type for summary
  const statementCounts = statements.reduce((acc, stmt) => {
    acc[stmt.statement_type] = (acc[stmt.statement_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="statements-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">Statements</h1>
          <p className="text-[#6C757D] mt-1">View and download your investor statements</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchStatements}
          className="border-gray-200"
          data-testid="refresh-statements-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">{statementCounts.monthly || 0}</p>
                <p className="text-xs text-[#6C757D]">Monthly</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <FileSpreadsheet className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">{statementCounts.quarterly || 0}</p>
                <p className="text-xs text-[#6C757D]">Quarterly</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <FileSpreadsheet className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">{statementCounts.annual || 0}</p>
                <p className="text-xs text-[#6C757D]">Annual</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1D2D2A]/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-[#1D2D2A]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">{statements.length}</p>
                <p className="text-xs text-[#6C757D]">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-[200px] border-gray-200" data-testid="type-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Statement type" />
              </SelectTrigger>
              <SelectContent className="z-50">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="monthly">Monthly Statement</SelectItem>
                <SelectItem value="quarterly">Quarterly Summary</SelectItem>
                <SelectItem value="annual">Annual Summary</SelectItem>
                <SelectItem value="adhoc">Ad-hoc</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full md:w-[280px] justify-start text-left font-normal border-gray-200"
                  data-testid="date-range-picker"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, 'dd MMM yyyy')} - {format(dateRange.to, 'dd MMM yyyy')}
                      </>
                    ) : (
                      format(dateRange.from, 'dd MMM yyyy')
                    )
                  ) : (
                    <span className="text-muted-foreground">Select date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-50" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from}
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-[#6C757D]"
              >
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Statements Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      ) : statements.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-[#ADB5BD] mb-4" />
            <p className="text-[#6C757D]">No statements found</p>
            {hasFilters && (
              <Button variant="link" onClick={clearFilters} className="mt-2 text-[#18A37C]">
                Clear filters to see all statements
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm overflow-hidden" data-testid="statements-list">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-[#6C757D]">
                <th className="px-4 py-3 font-medium">Period</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Chronological — newest first — sorted by period_end desc.
                  Generated-date column intentionally removed per admin ask. */}
              {[...statements]
                .sort((a, b) => new Date(b.period_end || 0) - new Date(a.period_end || 0))
                .map((statement) => (
                <tr
                  key={statement.id}
                  className="border-b last:border-b-0 hover:bg-gray-50 transition-colors"
                  data-testid={`statement-row-${statement.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        statement.statement_type === 'monthly' ? 'bg-blue-50' :
                        statement.statement_type === 'quarterly' ? 'bg-purple-50' :
                        statement.statement_type === 'annual' ? 'bg-green-50' :
                        'bg-gray-50'
                      }`}>
                        {getTypeIcon(statement.statement_type)}
                      </div>
                      <div>
                        <div className="font-medium text-[#212529]">{statement.period_label}</div>
                        <div className="text-xs text-[#6C757D]">
                          {formatDate(statement.period_start)} — {formatDate(statement.period_end)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`${getTypeColor(statement.statement_type)} border text-xs`}>
                      {getTypeLabel(statement.statement_type)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      className={`${
                        getStatus(statement) === 'available'
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : 'bg-amber-100 text-amber-700 border-amber-200'
                      } border`}
                    >
                      {getStatus(statement) === 'available' ? (
                        <><CheckCircle2 className="h-3 w-3 mr-1" /> Available</>
                      ) : (
                        <><Clock className="h-3 w-3 mr-1" /> Processing</>
                      )}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-200 hover:border-[#18A37C] hover:text-[#1D2D2A]"
                        onClick={() => handlePreview(statement)}
                        disabled={getStatus(statement) !== 'available'}
                        data-testid={`view-btn-${statement.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                        onClick={() => handleDownload(statement)}
                        disabled={getStatus(statement) !== 'available'}
                        data-testid={`download-btn-${statement.id}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {selectedStatement?.period_label} - {getTypeLabel(selectedStatement?.statement_type)}
            </DialogTitle>
            <DialogDescription>
              Generated on {formatDate(selectedStatement?.generated_date)}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-100 rounded-lg p-8 min-h-[500px] flex items-center justify-center">
            <div className="text-center">
              <FileText className="h-16 w-16 mx-auto text-[#6C757D] mb-4" />
              <p className="text-[#6C757D] mb-4">PDF Preview</p>
              <p className="text-sm text-[#ADB5BD]">
                In production, the actual PDF would be displayed here using a PDF viewer component.
              </p>
              <Button
                className="mt-4 bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                onClick={() => selectedStatement && handleDownload(selectedStatement)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StatementsPage;
